import { type PixiScene } from "./pixi_scene"
import { type CameraState, type ViewLocation } from "./coords"
import { renderGridToGraphic } from "./renderers"

export type ViewportSize = {
  app_width: number
  app_height: number
}

export type ViewBounds = {
  x: number
  y: number
  scale: number
}

export type ScaleViewport = {
  new_scale: number
  dpx: number
  dpy: number
}

export default class ViewportRenderer {
  private scene: PixiScene
  private resizeObserver: ResizeObserver | null = null

  constructor(scene: PixiScene) {
    this.scene = scene
  }

  canvas(): HTMLCanvasElement {
    return this.scene.app.view as HTMLCanvasElement
  }

  set_resize_target(dom_element: HTMLElement): void {
    this.scene.app.resizeTo = dom_element
    this.resizeObserver?.disconnect()
    this.resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        this.reposition_grid(this.camera_state(this.scene.app.stage.scale.x || 1))
        this.scene.request_render()
      })
    })
    this.resizeObserver.observe(dom_element)
  }

  screen_width(): number {
    return this.scene.app.screen.width
  }

  screen_height(): number {
    return this.scene.app.screen.height
  }

  camera_state(scale: number): CameraState {
    return {
      pan_x: this.scene.app.stage.x || 0,
      pan_y: this.scene.app.stage.y || 0,
      scale,
    }
  }

  view_bounds(scale: number): ViewBounds {
    return {
      x: this.scene.app.stage.x,
      y: this.scene.app.stage.y,
      scale,
    }
  }

  apply_view_bounds({ x, y, scale }: ViewBounds): void {
    this.scene.app.stage.x = x
    this.scene.app.stage.y = y
    this.scene.app.stage.scale.x = scale
    this.scene.app.stage.scale.y = scale
    this.reposition_grid(this.camera_state(scale))
  }

  reset_view({ app_width, app_height }: ViewportSize): void {
    this.scene.app.stage.x = app_width / 4
    this.scene.app.stage.y = app_height / 3
    this.scene.app.stage.scale.x = 1
    this.scene.app.stage.scale.y = 1
    this.reposition_grid(this.camera_state(1))
  }

  move_view({ vx, vy }: ViewLocation): void {
    this.scene.app.stage.x = vx
    this.scene.app.stage.y = vy
    this.reposition_grid(this.camera_state(this.scene.app.stage.scale.x || 1))
  }

  scale_view({ new_scale, dpx, dpy }: ScaleViewport): void {
    this.scene.app.stage.scale.x = new_scale
    this.scene.app.stage.scale.y = new_scale
    this.scene.app.stage.x += dpx * new_scale
    this.scene.app.stage.y += dpy * new_scale
    this.reposition_grid(this.camera_state(new_scale))
  }

  reposition_grid(camera_state: CameraState): void {
    this.scene.layers.grid.x = 0
    this.scene.layers.grid.y = 0
    renderGridToGraphic(
      this.scene.layers.grid,
      this.scene.is_dark_mode,
      camera_state,
      this.screen_width(),
      this.screen_height(),
    )
  }

  destroy(): void {
    this.resizeObserver?.disconnect()
    this.scene.app.destroy(true, {
      children: true,
      texture: true,
      baseTexture: true,
    })
  }
}
